import { createSelector, createSlice } from "@reduxjs/toolkit";
import { logout } from "../common/reducers/initContext";
import { dataHookFactory, dataHookWithCredentialsFactory, DefaultFailureAction, DefaultSuccessAction, initMapFactory, queryMapFactory } from "../common/reducers/utilities";
import { dateInRangeInclusive, maxDate, minDate } from "../common/utils/dateUtils";
import { aggregatingFetch } from "../common/hooks/networkHook";
import { ensureArray, groupBy } from "../common/utils/arrayUtilities";
import useCredentials from "../common/hooks/credentialsHook";
import { markFetched, registerEntityBasedFetch } from "../common/hooks/initHook";

const key = e => e.id;

export const expenseClaimsSlice = createSlice({
    name: 'expenseClaims',
    initialState: {
        types: [],
        data: {}
    },
    reducers: {
        setTypes: (state, action) => {
            state.types = action.payload;
        },
        set: (state, action) => {
            ensureArray(action.payload.ids).forEach(id => delete state.data[id]);
            ensureArray(action.payload.data).forEach(e => state.data[key(e)] = e);
        },
        addOrUpdate: (state, action) => {
            ensureArray(action.payload).forEach(e => state.data[key(e)] = e);
        },
        remove: (state, action) => {
            ensureArray(action.payload).forEach(e => delete state.data[key(e)]);
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => expenseClaimsSlice.getInitialState());
    }
});

// #region network operations
const visualizeSuccess = (verb, action) => DefaultSuccessAction(verb, 'expense_one', action);
const visualizeFailure = verb => DefaultFailureAction(verb, 'expense_one');

const fetchExpenseClaimTypes = network => network.get('/api/v1/expenseTypes', expenseClaimsSlice.actions.setTypes, visualizeFailure('get'));
const fetchExpenseClaimsByID = aggregatingFetch(
    id => network => network.post('/api/v2/fetchExpenses', [id], data => expenseClaimsSlice.actions.set({ ids: [id], data }), visualizeFailure('get')),
    ids => network => network.post('api/v2/fetchExpenses', ids, data => expenseClaimsSlice.actions.addOrUpdate({ ids, data }), visualizeFailure('get'))
)
const fetchExpenseClaimsInRange = aggregatingFetch(
    (start, end) => network => network.get(`/api/v2/expenses/${encodeURIComponent(start)}/${encodeURIComponent(end)}`, expenseClaimsSlice.actions.addOrUpdate, visualizeFailure('get')),
    keys => network => network.get(`/api/v2/expenses/${encodeURIComponent(minDate(keys, 0))}/${encodeURIComponent(maxDate(keys, 1))}`, expenseClaimsSlice.actions.addOrUpdate, visualizeFailure('get'))
);

export const confirmExpenseClaim = (network, id) => network.put(`/api/v1/expenses/approve/${window.encodeURIComponent(id)}`, {}, expenseClaimsSlice.actions.addOrUpdate, visualizeFailure('update'));
export const rejectExpenseClaim = (network, id) => network.put(`/api/v1/expenses/reject/${window.encodeURIComponent(id)}`, {}, expenseClaimsSlice.actions.addOrUpdate, visualizeFailure('update'));
export const updateExpenseClaim  = (network, claim) => network.put('/api/v1/expense', claim, expenseClaimsSlice.actions.addOrUpdate, visualizeFailure('update'));

export const createExpenseClaim = (network, claim) => {
    let data = new FormData();
    
    Object.entries(claim).forEach(([ field, value ]) => {
        if (!['approved', 'approval_user', 'approval_date'].includes(field)) {
            data.append(field, value)
        }
    });

    if (claim.file?.type) {
        data.append('mime', claim.file.type);
    }

    if (claim.approved && claim.approval_user && claim.approval_date) {
        data.append('approved', claim.approved);
        data.append('approval_user', claim.approval_user);
        data.append('approval_date', claim.approval_date);
    }
    
    network.postData('/api/v1/expense', data);
}

export const deleteExpenseClaim = (network, claim) =>  network.delete(`/api/v1/expense/${window.encodeURIComponent(claim.id)}`, expenseClaimsSlice.actions.remove, visualizeFailure('remove'));
// #endregion

// #region selectors
const selectTypes = state => state.expenseClaims.types;
const selectAllClaims = state => state.expenseClaims.data;
const selectAllClaimsByMember = createSelector([selectAllClaims], claims => groupBy(Object.values(claims), 'memberid'));
const selectMgrClaimsInRange = (start, end, mgrid) => createSelector([selectAllClaims], claims => Object.values(claims).filter(e => e.memberid !== mgrid && dateInRangeInclusive(e.date, { start, end })) ?? []);
const selectMgrPendingClaimsInRange = (start, end, mgrid) => createSelector([selectAllClaims], claims => Object.values(claims).filter(e => e.approved === null && e.memberid !== mgrid && dateInRangeInclusive(e.date, { start, end })) ?? []);
const selectStaffClaimsInRange = (start, end, memberid) => createSelector([selectAllClaimsByMember], claims => claims[memberid]?.filter(e => dateInRangeInclusive(e.date, { start, end })) ?? []);

// #endregion

const listen = {
    expenseCreated: (claim, { dispatch, websocket }) => {
        markFetched(websocket, 'expense', key(claim))
        dispatch(expenseClaimsSlice.actions.addOrUpdate(claim))
    },
    expenseUpdated: (claim, { dispatch }) => dispatch(expenseClaimsSlice.actions.addOrUpdate(claim)),
    expenseRemoved: (claim, { dispatch }) => dispatch(expenseClaimsSlice.actions.remove(claim))
}

const claimsInitByID = initMapFactory(fetchExpenseClaimsByID, listen);
registerEntityBasedFetch('expense', claimsInitByID);

const cleanup = { root: expenseClaimsSlice.name, all: state => Object.values(selectAllClaims(state)), removeAll: expenseClaimsSlice.actions.remove, key };

export const [useMgrExpenseClaimsInRange] = dataHookFactory(selectMgrClaimsInRange, queryMapFactory(fetchExpenseClaimsInRange, 'id', claimsInitByID, listen, { cleanup }));
export const [useMgrPendingExpenseClaimsInRange] = dataHookFactory(selectMgrPendingClaimsInRange, queryMapFactory(fetchExpenseClaimsInRange, 'id', claimsInitByID, listen, { cleanup }));
export const [useStaffExpenseClaimsInRange] = dataHookWithCredentialsFactory(selectStaffClaimsInRange, queryMapFactory(fetchExpenseClaimsInRange, 'id', claimsInitByID, listen), {cleanup, condition: credentials => credentials?.hasRole('member') });

export const useSelfClaimsInRange = (start, end) => { 
    const credentials = useCredentials();
    return useStaffExpenseClaimsInRange(start, end, credentials.memberID);
}

const viewRoles = ['administrator', 'member manager', 'auditor', 'member auditor'];
export const canRouteExpenseClaims = ({credentials, clientConfig}) => credentials.hasRole(viewRoles) && clientConfig.enableExpenses;

export const [useExpenseClaimTypes,realizeExpenseClaimTypes] = dataHookFactory(selectTypes, { run: fetchExpenseClaimTypes, immortal: true });

const reducer = { name: expenseClaimsSlice.name, reducer: expenseClaimsSlice.reducer }
export default reducer;